import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Divider, Empty, Input, Modal, Space, Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { useNavigate } from 'react-router-dom'
import { quickOptions } from '../../../../mock/dataMock'
import ResultEntity from '../../search/ResultEntity'
import User from '../../search/User'
import { IssueFilterCriteria, useFilterIssue } from '../../../hooks/issues/useFilterIssue'
import { useSearch } from '../../../contexts/SearchContext'

interface HeaderSearchModalProps {
  open: boolean
  onClose: () => void
  initialQuery?: string
}

const HeaderSearchModal: React.FC<HeaderSearchModalProps> = ({ open, onClose, initialQuery = '' }) => {
  const navigate = useNavigate()
  const { handleHeaderSearch } = useSearch()
  const inputRef = useRef<any>(null)

  const [query, setQuery] = useState(initialQuery)
  const [criteria, setCriteria] = useState<IssueFilterCriteria>({})
  const [activeIndex, setActiveIndex] = useState(-1)
  const itemRefs = useRef<Array<HTMLDivElement | null>>([])

  const { data: searchResponse, refetch, isFetching } = useFilterIssue(criteria)

  const debouncedApiSearch = useMemo(
    () =>
      debounce((text: string) => {
        if (text.length > 0) {
          setCriteria({ searchText: text, offset: 0, limit: 50, skipAggregation: true })
        } else {
          setCriteria({})
        }
      }, 300),
    [],
  )

  useEffect(() => () => debouncedApiSearch.cancel(), [debouncedApiSearch])

  // Re-fetch whenever the criteria changes (debounce ensures stability).
  useEffect(() => {
    if (criteria.searchText) refetch()
  }, [criteria, refetch])

  // Reset the query each time the modal is opened with a different initial value.
  useEffect(() => {
    if (open) {
      setQuery(initialQuery)
      if (initialQuery) {
        setCriteria({ searchText: initialQuery, offset: 0, limit: 50, skipAggregation: true })
      }
    }
  }, [open, initialQuery])

  const handleChange = useCallback(
    (text: string) => {
      setQuery(text)
      debouncedApiSearch(text)
    },
    [debouncedApiSearch],
  )

  const handleSelect = useCallback(
    (id: string) => {
      navigate(`/detail/${id}`)
      onClose()
    },
    [navigate, onClose],
  )

  const results = (searchResponse?.data ?? []) as any[]
  const showResults = !!criteria.searchText
  const noResults = showResults && !isFetching && results.length === 0

  // Reset active row whenever the result set changes. No row is preselected so
  // pressing Enter runs the header search (results land in IssueGrid); the user
  // opts into navigation by pressing ArrowDown first.
  useEffect(() => {
    setActiveIndex(-1)
    itemRefs.current = itemRefs.current.slice(0, results.length)
  }, [results])

  // Keep the active row visible.
  useEffect(() => {
    if (activeIndex < 0) return
    const el = itemRefs.current[activeIndex]
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const handleEnter = useCallback(() => {
    if (activeIndex >= 0 && results[activeIndex]) {
      handleSelect(results[activeIndex].id)
      return
    }
    if (query.trim()) {
      handleHeaderSearch(query, false)
      navigate('/issues')
      onClose()
    }
  }, [activeIndex, results, handleSelect, query, handleHeaderSearch, navigate, onClose])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Home') {
        // Let Shift/Ctrl/Meta/Alt + Home reach the input for native text selection.
        if (results.length > 0 && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault()
          setActiveIndex(0)
        }
      } else if (e.key === 'End') {
        if (results.length > 0 && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault()
          setActiveIndex(results.length - 1)
        }
      }
    },
    [results.length],
  )

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      destroyOnHidden
      width={640}
      centered={false}
      className="gt-search-modal"
      mask={{closable: true}}
      styles={{
        body: { padding: 0 },
      }}
      afterOpenChange={(o) => {
        if (o) inputRef.current?.focus()
      }}
    >
      <div className="gt-search-modal-input-row">
        <Input
          ref={inputRef}
          variant="borderless"
          size="large"
          prefix={<SearchOutlined className="gt-search-modal-prefix" />}
          placeholder="Search incidents, users…"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onPressEnter={handleEnter}
          onKeyDown={handleInputKeyDown}
          allowClear
          suffix={<kbd className="gt-search-modal-kbd">Esc</kbd>}
        />
      </div>
      <Divider style={{ margin: 0 }} />

      <div className="gt-search-modal-body">
        <div className="gt-search-modal-quick">
          <Space size={8} wrap>
            {quickOptions.map((opt) => (
              <Button
                key={opt.id}
                shape="round"
                size="small"
                onClick={() => handleChange(opt.name)}
              >
                {opt.name}
              </Button>
            ))}
          </Space>
        </div>

        {showResults && (
          <>
            <Divider style={{ margin: 0 }} />
            <div className="gt-search-modal-results">
              {isFetching && results.length === 0 ? (
                <div className="gt-search-modal-loading">
                  <Spin size="small" />
                </div>
              ) : noResults ? (
                <Empty description={`No results for "${query}"`} style={{ padding: 24 }} />
              ) : (
                <div role="listbox" aria-label="Search results">
                  {results.map((item, idx) => {
                    const active = idx === activeIndex
                    return (
                      <div
                        key={item.id}
                        role="option"
                        aria-selected={active}
                        ref={(el) => {
                          itemRefs.current[idx] = el
                        }}
                        className={
                          'gt-search-modal-result-item' +
                          (active ? ' gt-search-modal-result-item-active' : '')
                        }
                        onClick={() => handleSelect(item.id)}
                        onMouseEnter={() => setActiveIndex(idx)}
                      >
                        {item.typeEntity === 'USER' ? (
                          <User user={item} match={query} />
                        ) : (
                          <ResultEntity entity={item} match={query} />
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Divider style={{ margin: 0 }} />
      <div className="gt-search-modal-footer">
        <span>{query ? `All results for "${query}"` : 'Type to search'}</span>
        <kbd className="gt-search-modal-kbd">Press ENTER</kbd>
      </div>
    </Modal>
  )
}

export default HeaderSearchModal
